import { useIsMobile } from "@library/shared";
import { useMemo } from "react";
import { TABLE_ROW_MIN_HEIGHT } from "../constants";
import { useTableCtx } from "../ctx";
import type { ITableCellStyleSettings } from "../hooks/use-table-cell-styles";
import { useFormulaHighlight } from "../hooks/use-formula-highlight";
import type { ITableSpecificSettings } from "../types";
import {
  computeFrozenRowTops,
  resolveFrozenRowCount,
} from "../utils/compute-frozen-rows";
import {
  evaluateCalculation,
  formatCalculationResult,
} from "../utils/evaluate-calculation";
import { getMobileColWidths } from "../utils/mobile-col-widths";
import { TableCell, type ITableCellEditorFlags } from "./table-cell";

export const TableRows = () => {
  const isMobile = useIsMobile();
  const {
    entityId,
    editorData,
    elementSettings,
    rowOrder,
    resolveCellContent,
    cellChildren,
    cellChildIds,
    InCellStack,
    CellDropTarget,
  } = useTableCtx();
  const {
    tableRows,
    tableCols,
    tableData,
    tableRowHeights,
    tableColWidths,
    // Settings needed by TableCell / useTableCellStyles / useTableCellBorders:
    tableCellVerticalAlignment,
    tableHasHeader,
    tableHeaderPosition,
    tableHasAlternatingRows,
    tableCellBgColor,
    tableHeaderBgColor,
    tableAlternatingRowsBgColor,
    tableCellTextColor,
    tableHeaderTextColor,
    tableAlternatingRowsTextColor,
    tableCellTextAlignment,
    tableCellTextOrientation,
    tableCellFontSize,
    tableCellBorderColor,
    tableCellBorderWidth,
    tableCellBorderStyle,
    tableAlternatingRowsBorderColor,
    tableHeaderBorderColor,
    tableFrozenRowsCount,
    round,
  } = elementSettings;

  // Bundle all setting fields TableCell reads into a single object with a
  // stable identity. Its reference only changes when one of these specific
  // fields changes — NOT when tableData does (cell edits). That's what lets
  // TableCell.memo bail out for unchanged cells during content edits, which
  // is the dominant cost of typing on large tables.
  const cellSettings = useMemo<
    ITableCellStyleSettings &
      Pick<ITableSpecificSettings, "tableCellVerticalAlignment">
  >(
    () => ({
      tableCellVerticalAlignment,
      tableHasHeader,
      tableHeaderPosition,
      tableRows,
      tableCols,
      tableHasAlternatingRows,
      tableCellBgColor,
      tableHeaderBgColor,
      tableAlternatingRowsBgColor,
      tableCellTextColor,
      tableHeaderTextColor,
      tableAlternatingRowsTextColor,
      tableCellTextAlignment,
      tableCellTextOrientation,
      tableCellFontSize,
      tableCellBorderColor,
      tableCellBorderWidth,
      tableCellBorderStyle,
      tableAlternatingRowsBorderColor,
      tableHeaderBorderColor,
      round,
    }),
    [
      tableCellVerticalAlignment,
      tableHasHeader,
      tableHeaderPosition,
      tableRows,
      tableCols,
      tableHasAlternatingRows,
      tableCellBgColor,
      tableHeaderBgColor,
      tableAlternatingRowsBgColor,
      tableCellTextColor,
      tableHeaderTextColor,
      tableAlternatingRowsTextColor,
      tableCellTextAlignment,
      tableCellTextOrientation,
      tableCellFontSize,
      tableCellBorderColor,
      tableCellBorderWidth,
      tableCellBorderStyle,
      tableAlternatingRowsBorderColor,
      tableHeaderBorderColor,
      round,
    ],
  );

  // Editor slice: bail-out booleans for each cell. We pre-compute the
  // selected cell ID set once per selection change so the row loop doesn't
  // call .includes() per cell. Use activeCellsIds (not selection.cellsIds)
  // because non-contiguous Cmd/Ctrl+click picks suppress the rect selection
  // but the cells are still selected and should show the per-cell outline.
  const selectionIds = useMemo(() => {
    if (!editorData?.activeCellsIds?.length) return null;
    return new Set(editorData.activeCellsIds);
  }, [editorData?.activeCellsIds]);

  const editorCtx = useMemo(() => {
    if (!editorData) return null;
    return {
      startCellId: editorData.selection?.startCellId,
      activeCellId: editorData.activeCellId,
      isTableActive: editorData.isTableActive,
      navigationMode: editorData.navigationMode,
      FroalaEditor: editorData.FroalaEditor,
      TableCellEditorObserver: editorData.TableCellEditorObserver,
      startFormulaEdit: editorData.startFormulaEdit,
      updateFormulaEdit: editorData.updateFormulaEdit,
      stopFormulaEdit: editorData.stopFormulaEdit,
      cellChildrenIndex: editorData.cellChildrenIndex,
    };
  }, [
    editorData?.selection?.startCellId,
    editorData?.activeCellId,
    editorData?.isTableActive,
    editorData?.navigationMode,
    editorData?.FroalaEditor,
    editorData?.TableCellEditorObserver,
    editorData?.startFormulaEdit,
    editorData?.updateFormulaEdit,
    editorData?.stopFormulaEdit,
    editorData?.cellChildrenIndex,
  ]);

  const { getFormulaHighlight } = useFormulaHighlight({
    entityId,
    tableData,
    tableCols,
    tableRows,
    startCellId: editorCtx?.startCellId,
    formulaEdit: editorData?.formulaEdit ?? null,
  });

  const cellsById = useMemo(
    () => new Map(tableData.map((c) => [c.id, c])),
    [tableData],
  );

  const colGroup = useMemo(() => {
    const mobileWidths = isMobile
      ? getMobileColWidths(tableData, tableCols, tableRows)
      : null;
    return Array.from({ length: tableCols }, (_, col) => {
      const width = mobileWidths
        ? mobileWidths[col]
        : tableColWidths[col]
          ? `${tableColWidths[col]}%`
          : undefined;

      return <col key={col} style={{ width }} />;
    });
  }, [tableCols, tableColWidths, isMobile, tableData, tableRows]);

  const iterationOrder = useMemo(
    () => rowOrder ?? Array.from({ length: tableRows }, (_, i) => i),
    [rowOrder, tableRows],
  );

  const frozenCount = resolveFrozenRowCount(
    tableFrozenRowsCount,
    iterationOrder.length,
  );
  const frozenRowTops = useMemo(
    () => computeFrozenRowTops(frozenCount, iterationOrder, tableRowHeights),
    [frozenCount, iterationOrder, tableRowHeights],
  );

  const rows = useMemo(() => {
    return iterationOrder.map((row, visualIdx) => {
      const isFrozenRow = visualIdx < frozenCount;
      const frozenStickyTop = isFrozenRow
        ? frozenRowTops[visualIdx]
        : undefined;
      const isFrozenBandEdge = isFrozenRow && visualIdx === frozenCount - 1;
      return (
        <tr
          key={row}
          aria-rowindex={visualIdx + 1}
          style={{ height: tableRowHeights[row] ?? TABLE_ROW_MIN_HEIGHT }}
        >
          {Array.from({ length: tableCols }, (_, col) => {
            const cellIdx = row * tableCols + col;
            const cell = tableData[cellIdx];

            if (!cell) {
              return <td key={col} />;
            }

            if (cell.mergedInto) {
              return null;
            }

            // Evaluate calc cells against the current (post-sort/filter) row order.
            let calcDisplay: string | undefined;
            if (cell.calculation) {
              const result = evaluateCalculation(cell.calculation.formula, {
                tableData,
                tableCols,
                tableRows,
                rowOrder: iterationOrder,
                cellCoord: { col, row },
              });
              calcDisplay = formatCalculationResult(result);
            }

            const idAt = (r: number, c: number) =>
              r >= 0 && r < tableRows && c >= 0 && c < tableCols
                ? tableData[r * tableCols + c]?.id
                : undefined;

            const {
              formulaCrossingTop,
              formulaCrossingBottom,
              formulaCrossingLeft,
              formulaCrossingRight,
              formulaCrossingGreenTop,
              formulaCrossingGreenBottom,
              formulaCrossingGreenLeft,
              formulaCrossingGreenRight,
              formulaEditBuffer,
              isFormulaReferenced,
            } = getFormulaHighlight(cell, row, col);

            let editor: ITableCellEditorFlags | undefined;
            if (editorCtx) {
              const isInSelection = selectionIds?.has(cell.id) ?? false;
              // `crossing*`: selection-perimeter edge flags, computed for every
              // cell (see ITableCellEditorFlags.crossingTop for the rationale).
              const sel = (id: string | undefined) =>
                !!id && !!selectionIds?.has(id);
              const aboveIn = sel(idAt(row - 1, col));
              const belowIn = sel(idAt(row + 1, col));
              const leftIn = sel(idAt(row, col - 1));
              const rightIn = sel(idAt(row, col + 1));
              // Contiguous selection draws one overlay rect; suppress per-cell
              // borders to avoid doubling.
              const selectionOverlayActive = !!editorData?.selection;
              editor = {
                isActive: editorCtx.startCellId === cell.id,
                isRovingFocus: editorCtx.activeCellId === cell.id,
                hasInCellChildren:
                  !!editorCtx.cellChildrenIndex?.[cell.id]?.length,
                isInSelection,
                isTableActive: editorCtx.isTableActive,
                hasRovingFocus: !!editorCtx.activeCellId,
                navigationMode: editorCtx.navigationMode,
                crossingTop:
                  !selectionOverlayActive && isInSelection !== aboveIn,
                crossingBottom:
                  !selectionOverlayActive && isInSelection !== belowIn,
                crossingLeft:
                  !selectionOverlayActive && isInSelection !== leftIn,
                crossingRight:
                  !selectionOverlayActive && isInSelection !== rightIn,
                selectionOverlayActive,
                FroalaEditor: editorCtx.FroalaEditor,
                TableCellEditorObserver: editorCtx.TableCellEditorObserver,
                startFormulaEdit: editorCtx.startFormulaEdit,
                updateFormulaEdit: editorCtx.updateFormulaEdit,
                stopFormulaEdit: editorCtx.stopFormulaEdit,
              };
            }

            // Single-stroke shared borders: a cell suppresses its own top/left
            // border only when the neighbour above/left actually draws that edge.
            // KNOWN LIMITATION: only the neighbour at this cell's anchor slot is
            // probed, so suppression is all-or-nothing across a merged span whose
            // neighbours disagree on the shared edge. The popover mirror-writes
            // keep facing sides in sync, so this state isn't reachable from the
            // UI today; revisit if another write path stops mirroring.
            const resolveW = (w?: number) => w ?? tableCellBorderWidth ?? 0;
            const ownerOf = (c: (typeof tableData)[number] | undefined) =>
              c && c.mergedInto ? cellsById.get(c.mergedInto) : c;
            const aboveDataRow =
              visualIdx > 0 ? iterationOrder[visualIdx - 1] : undefined;
            const topOwner = ownerOf(
              aboveDataRow !== undefined
                ? tableData[aboveDataRow * tableCols + col]
                : undefined,
            );
            const leftOwner = ownerOf(
              col > 0 ? tableData[row * tableCols + (col - 1)] : undefined,
            );
            const topNeighborDrawsBottom =
              !!topOwner && resolveW(topOwner.borderBottomWidth) > 0;
            const leftNeighborDrawsRight =
              !!leftOwner && resolveW(leftOwner.borderRightWidth) > 0;

            return (
              <TableCell
                key={cell.id}
                idx={cellIdx}
                row={row}
                visualRow={visualIdx}
                col={col}
                cell={cell}
                entityId={entityId}
                settings={cellSettings}
                editor={editor}
                frozenStickyTop={frozenStickyTop}
                isFrozenBandEdge={isFrozenBandEdge}
                topNeighborDrawsBottom={topNeighborDrawsBottom}
                leftNeighborDrawsRight={leftNeighborDrawsRight}
                calcDisplay={calcDisplay}
                formulaEditBuffer={formulaEditBuffer}
                isFormulaReferenced={isFormulaReferenced}
                formulaCrossingTop={formulaCrossingTop}
                formulaCrossingBottom={formulaCrossingBottom}
                formulaCrossingLeft={formulaCrossingLeft}
                formulaCrossingRight={formulaCrossingRight}
                formulaCrossingGreenTop={formulaCrossingGreenTop}
                formulaCrossingGreenBottom={formulaCrossingGreenBottom}
                formulaCrossingGreenLeft={formulaCrossingGreenLeft}
                formulaCrossingGreenRight={formulaCrossingGreenRight}
                resolveCellContent={resolveCellContent}
                staticChildren={cellChildren?.[cell.id]}
                hasStackChildren={!!cellChildIds?.[cell.id]?.length}
                InCellStack={InCellStack}
                CellDropTarget={CellDropTarget}
              />
            );
          })}
        </tr>
      );
    });
  }, [
    iterationOrder,
    frozenCount,
    frozenRowTops,
    tableData,
    tableRows,
    tableCols,
    tableRowHeights,
    tableCellBorderWidth,
    cellsById,
    entityId,
    cellSettings,
    editorCtx,
    editorData?.selection,
    selectionIds,
    getFormulaHighlight,
    resolveCellContent,
    cellChildren,
    cellChildIds,
    InCellStack,
    CellDropTarget,
  ]);

  return (
    <>
      <colgroup>{colGroup}</colgroup>
      <tbody>{rows}</tbody>
    </>
  );
};
